<template>
  <div :id="id" class="marketingRowContainer col-12" :class="[backgroundColor, textSide, hasSlot ? 'hasSlot' : 'noSlot']">
    <div class="container" :class="{'border-top': hasBorderTop}">
      <div class="marketingContent d-flex" :class="textSide === 'left' ? 'flex-row' : 'flex-row-reverse'">
        <div class="textSide flex-1 d-flex flex-column justify-content-center align-items-start">
          <component v-bind:is="titleType" v-for="(titleText, index) in title" :key="`title-${index}`" class="rowHeading" v-html="titleText"></component>
          <template v-if="bodyIsHTML">
            <div v-for="(bodyHTML, index) in body" :key="`bodyhtml-${index}`" class="w-100" v-html="bodyHTML" />
          </template>
          <template v-else>
            <p v-for="(bodyText, index) in body" :key="`body-${index}`">
              {{ bodyText }}
            </p>
          </template>
          <div v-if="canShowButtonSection" class="buttonControls flex-center">
            <button
              v-if="defaultButtonEmit && defaultButtonText"
              class="btn btn-default "
              @click="$emit('defaultClicked', defaultButtonEmit)"
            >
              {{ defaultButtonText }}
            </button>
            <a
              v-if="defaultButtonLink && defaultButtonText"
              class="btn btn-default "
              :href="defaultButtonLink"
            >
              {{ defaultButtonText }}
            </a>
            <button
              v-if="primaryButtonEmit && primaryButtonText"
              class="btn btn-primary "
              @click="$emit('defaultClicked', primaryButtonEmit)"
            >
              {{ primaryButtonText }}
            </button>
            <a
              v-if="primaryButtonLink && primaryButtonText"
              class="btn btn-primary "
              :href="primaryButtonLink"
            >
              {{ primaryButtonText }}
            </a>
            <button
              v-if="darkButtonEmit && darkButtonText"
              class="btn btn-dark "
              @click="$emit('defaultClicked', darkButtonEmit)"
            >
              {{ darkButtonText }}
            </button>
            <a
              v-if="darkButtonLink && darkButtonText"
              class="btn btn-dark "
              :href="darkButtonLink"
            >
              {{ darkButtonText }}
            </a>
          </div>
        </div>
        <div v-if="hasSlot" class="assetSide flex-1 d-flex justify-content-center align-items-center" :class="{'noBg': noAssetBackground}">
          <div class="assetAspectWrapper">
            <div class="assetContent">
              <slot name="marketingContent"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MarketingRow',
  props: {
    /** Either 'white' or 'gray' color options */
    backgroundColor: {
      type: String,
      required: false,
      default: 'white',
    },
    darkButtonEmit: {
      type: String,
      required: false,
    },
    darkButtonLink: {
      type: String,
      required: false,
    },
    darkButtonText: {
      type: String,
      required: false,
    },
    id: {
      type: String,
      required: false,
    },
    /** Either 'left' or 'right' label option */
    textSide: {
      type: String,
      required: false,
      default: 'left',
    },
    title: {
      type: Array,
      required: true,
    },
    hasBorderTop: {
      type: Boolean,
      required: false,
      default: false,
    },
    titleType: {
      type: String,
      required: false,
      default: 'h4'
    },
    body: {
      type: Array,
      required: true,
    },
    bodyIsHTML: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultButtonEmit: {
      type: String,
      required: false,
    },
    defaultButtonLink: {
      type: String,
      required: false,
    },
    defaultButtonText: {
      type: String,
      required: false,
    },
    noAssetBackground: {
      type: Boolean,
      required: false,
      default: false
    },
    primaryButtonLink: {
      type: String,
      required: false,
    },
    primaryButtonText: {
      type: String,
      required: false,
    },
    primaryButtonEmit: {
      type: String,
      required: false,
    },
  },
  computed: {
    hasSlot() {
      return Object.keys(this.$slots).length > 0;
    },
    canShowButtonSection() {
      return (
        ((this.defaultButtonEmit || this.defaultButtonLink) && this.defaultButtonText)
        || ((this.darkButtonEmit || this.darkButtonLink) && this.darkButtonText)
        || ((this.primaryButtonEmit || this.primaryButtonLink) && this.primaryButtonText)
      );
    }
  }
};
</script>

<style lang="scss">
  @import '@sass/vue-imports.scss';

  .marketingRowContainer {
    width: 100%;

    .container {
      padding: 40px 0;
    }

    &.noSlot {
      .marketingContent {
        justify-content: center;
        .textSide {
          @include media-breakpoint-up(md) {
            max-width: 50%;
          }

          .rowHeading {
            margin: 0 auto 25px;
          }
          .buttonControls {
            margin: 35px auto 0;
          }
        }
      }
    }

    &.left {
      .textSide {
        margin-right: 30px;
        @include media-breakpoint-down(sm) {
          margin-right: 0;
        }
      }
    }
    &.right {
      .assetSide {
        margin-right: 30px;
        @include media-breakpoint-down(sm) {
          margin-right: 0;
        }
      }
    }

    &.white {
      background: #fff;
    }

    &.gray {
      background: linear-gradient(120.06deg, #F8F8F8 0%, rgba(248, 248, 248, 0.5) 100%);
    }

    .overflowImage {
      height: 130% !important;
      width: 120% !important;
      max-height: 130% !important;
      max-width: 120% !important;
      margin-left: -10%;
      margin-top: -10%;
    }

    .marketingContent {
      .assetSide {
        background: radial-gradient(96.09% 96.09% at 21.74% 3.91%, #FFFFFF 0%, #F2F2F2 22.39%, #FAFBFB 45.41%, #F2F2F2 71.14%, #FAFBFB 100%);
        border-radius: 25px;
        overflow: hidden;

        &.noBg {
          background: transparent;
        }

        .assetAspectWrapper {
          position: relative;
          padding-top: 85%;
          width: 100%;
          .assetContent {
            width: 100%;
            height: 100%;
            position: absolute;
            left: 0;
            top: 0;

            >img {
              object-fit: cover;
              height: 100%;
              width: 100%;

              &.contain {
                border-radius: 25px;
                object-fit: contain;
              }
            }
          }
        }
      }

      .rowHeading {
        margin: 0;
        &:last-of-type {
          margin-bottom: 25px;
        }
      }

      p {
        margin: 0;
        & + p {
          margin-top: 30px;
        }
      }

      .buttonControls {
        margin-top: 35px;
        .btn + .btn {
          margin-left: 10px;
        }
      }

      @include media-breakpoint-down(sm) {
        flex-direction: column-reverse !important;
        .assetSide {
          margin-bottom: 40px;
          border-radius: 0;
          border-top-left-radius: 25px;
          .assetAspectWrapper {
            padding-top: 75%;
          }
        }

        .textSide {

          .rowHeading {
            text-align: center;
            width: 100%;
          }
          h1.rowHeading {
            font-size: 28px;
          }
          .buttonControls {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            width: 100%;

            .btn {
              width: 250px;
            }

            .btn + .btn {
              margin-left: 0;
              margin-top: 10px;
            }
          }
        }
      }
    }

  }
</style>
